/* Fuel · every refill checked against the kilometres the bus drove.
   Design: ConsoleFuel.dc.html, plus the 3-step "Zana shows you around" tour
   from ConsoleZanaGuide.dc.html (header button or ?tour=1; Esc closes).
   Reads only: refills drivers logged, runs (last 30 days) and each bus's own
   normal (benchmarkKmPerL). */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFleet } from '../data/FleetData';
import type { Bus } from '../data/model';
import { usePage, useShell } from '../Shell';
import {
  CSS, Empty, PageBody, PageSkeleton, StaleBanner, ZanaMark, ZanaNote,
  dayMonth, n0, plateBeside, plural, useGo,
} from '../kit';
import { notLoggingBuses } from '../data/urgent';
import { platePlain } from '../data/zana';
import type { ExplainPoint } from '../zana/Explain';

const DAY = 86400000;
type Period = '7' | '30' | 'term';

interface BusFuel { bus: Bus; kmpl: number | null; normal: number | null; km: number; litres: number }

const TIP: CSS = {
  position: 'absolute', zIndex: 5, width: '300px', maxWidth: 'calc(100vw - 48px)', background: 'var(--tip)', color: 'var(--tip-ink)',
  borderRadius: '12px', padding: '14px 16px', boxShadow: '0 18px 40px -16px rgba(17,21,32,.5)', display: 'flex', flexDirection: 'column', gap: '6px',
};
const RING: CSS = {
  boxShadow: '0 0 0 3px var(--z), 0 0 0 9px rgba(111,79,240,.16)', borderRadius: '12px', zIndex: 4, position: 'relative',
  transition: 'box-shadow 200ms cubic-bezier(0,0,.2,1)',
};

const TourCard: React.FC<{ step: number; title: string; body: string; style: CSS }> = ({ step, title, body, style }) => (
  <div className="zin-a" role="dialog" aria-label={`Zana, step ${step + 1} of 3: ${title}`} style={{ ...TIP, ...style }}>
    <span className="row" style={{ gap: '8px', fontSize: '12px', color: '#C9C2FF', fontWeight: '600' }}>
      <span aria-hidden="true" style={{ width: '16px', height: '16px', borderRadius: '5px', background: 'var(--z)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '2px', flex: 'none' }}>
        <i style={{ width: '3px', height: '4px', borderRadius: '3px', background: '#fff', display: 'block' }} />
        <i style={{ width: '3px', height: '4px', borderRadius: '3px', background: '#fff', display: 'block' }} />
      </span>
      Zana · step {step + 1} of 3
    </span>
    <b style={{ fontSize: '15px', lineHeight: '20px' }}>{title}</b>
    <span style={{ fontSize: '14px', lineHeight: '20px', color: '#D5D9DE' }}>{body}</span>
  </div>
);

const Fuel: React.FC = () => {
  const f = useFleet();
  const { ask, openAddBus } = useShell();
  const go = useGo();
  const [params, setParams] = useSearchParams();
  const [period, setPeriod] = useState<Period>('30');

  const now = Date.now();
  const termStart = f.org.termStart && f.org.termStart.getTime() < now ? f.org.termStart.getTime() : null;
  const start = period === '7' ? now - 7 * DAY : period === '30' ? now - 30 * DAY : (termStart ?? now - 30 * DAY);
  const span = now - start;
  // Runs are loaded for the last 30 days only, so km-based numbers never reach further back.
  const kmStart = Math.max(start, now - 30 * DAY);
  const kmClamped = kmStart > start + DAY;

  const inWin = useCallback((at: Date | null, from: number, to: number) => !!at && at.getTime() >= from && at.getTime() < to, []);

  const refillsWin = useMemo(() => f.refills.filter((r) => inWin(r.at, start, now + 1)), [f.refills, inWin, start, now]);
  const spend = refillsWin.reduce((s, r) => s + (r.amount || 0), 0);
  const litres = refillsWin.reduce((s, r) => s + (r.litres || 0), 0);
  // The previous window of the same length (refills cover 120 days; Term has no "before").
  const prevSpend = period !== 'term'
    ? f.refills.filter((r) => inWin(r.at, start - span, start)).reduce((s, r) => s + (r.amount || 0), 0) : 0;
  const spendDelta = period !== 'term' && prevSpend > 0 ? Math.round(((spend - prevSpend) / prevSpend) * 100) : null;

  /* km per litre per bus over the window (30 days = the same figure the Buses page shows) */
  const kmOf = useCallback((busId: string, from: number, to: number) => f.runs
    .filter((r) => r.busId === busId && r.km != null && r.km > 0 && r.date.getTime() >= from && r.date.getTime() < to)
    .reduce((s, r) => s + (r.km as number), 0), [f.runs]);
  const litresOf = useCallback((busId: string, from: number, to: number) => f.refills
    .filter((r) => r.busId === busId && inWin(r.at, from, to))
    .reduce((s, r) => s + (r.litres || 0), 0), [f.refills, inWin]);

  const perBus = useMemo<BusFuel[]>(() => f.buses.map((b) => {
    if (period === '30' || (period === 'term' && kmStart <= now - 30 * DAY + DAY)) {
      return { bus: b, kmpl: b.kmpl, normal: b.kmplNormal, km: b.km30 || 0, litres: litresOf(b.id, now - 30 * DAY, now + 1) };
    }
    const km = kmOf(b.id, kmStart, now + 1);
    const l = litresOf(b.id, kmStart, now + 1);
    return { bus: b, kmpl: km > 0 && l > 0 ? km / l : null, normal: b.kmplNormal, km, litres: l };
  }), [f.buses, period, kmStart, now, kmOf, litresOf]);

  const ratio = (x: BusFuel) => (x.kmpl as number) / (x.normal as number);
  const withKmpl = perBus.filter((x) => x.kmpl != null);
  const ranked = [
    ...withKmpl.filter((x) => x.normal != null).sort((a, b) => ratio(a) - ratio(b)),
    ...withKmpl.filter((x) => x.normal == null).sort((a, b) => (a.kmpl as number) - (b.kmpl as number)),
  ];
  const noKmpl = perBus.length - withKmpl.length;
  const over = ranked.filter((x) => x.normal != null && (x.kmpl as number) < (x.normal as number) * 0.9);
  const scale = Math.max(1, ...ranked.map((x) => Math.max(x.kmpl as number, x.normal ?? 0))) * 1.125;

  /* "about Ksh X extra": extra litres at each bus's normal × the average price drivers paid */
  const priced = refillsWin.filter((r) => (r.litres || 0) > 0 && (r.amount || 0) > 0);
  const pricePerL = priced.length ? priced.reduce((s, r) => s + (r.amount as number), 0) / priced.reduce((s, r) => s + (r.litres as number), 0) : null;
  const extraL = over.reduce((s, x) => s + (x.km / (x.kmpl as number) - x.km / (x.normal as number)), 0);
  const extraKsh = pricePerL && extraL > 0 ? extraL * pricePerL : null;

  const fleetKm = perBus.reduce((s, x) => s + (x.kmpl != null ? x.km : 0), 0);
  const fleetL = perBus.reduce((s, x) => s + (x.kmpl != null ? x.litres : 0), 0);
  const fleetKmpl = fleetKm > 0 && fleetL > 0 ? fleetKm / fleetL : null;
  let fleetPrev: number | null = null;
  if (period === '7') {
    const km = f.buses.reduce((s, b) => s + kmOf(b.id, now - 14 * DAY, now - 7 * DAY), 0);
    const l = f.buses.reduce((s, b) => s + litresOf(b.id, now - 14 * DAY, now - 7 * DAY), 0);
    fleetPrev = km > 0 && l > 0 ? km / l : null;
  }
  const kmplDelta = fleetKmpl != null && fleetPrev != null ? fleetKmpl - fleetPrev : null;

  const notLogging = notLoggingBuses(f, now);

  const periodWord = period === '7' ? 'Last 7 days' : period === '30' ? 'Last 30 days' : 'This term';
  const extraWhen = period === '7' ? 'this week' : period === '30' ? 'in the last 30 days' : 'this term';
  const worst = over[0]?.bus;

  /* What Zana explains on this page, worded from the figures shown. */
  const recent = refillsWin.slice(0, 6);
  const highN = recent.filter((r) => r.verdict === 'high').length;
  const points: ExplainPoint[] = f.buses.length ? [{
    key: 'fu-spend', label: 'Estimated spend', mark: 'drivers logged',
    short: `Ksh ${n0(spend)} is what drivers logged for ${plural(refillsWin.length, 'refill')}, ${periodWord.toLowerCase()}.`,
    means: 'Each refill a driver logs in the Trasse app, with what it cost. A fill nobody logs is not counted, so this is an estimate.',
  }, {
    key: 'fu-bars', label: 'Km per litre, by bus', mark: 'its own normal',
    short: worst && over[0] ? `${worst.name} does ${(over[0].kmpl as number).toFixed(1)} km per litre against its own normal of ${(over[0].normal as number).toFixed(1)}.` : 'Every bus is close to its own normal.',
    means: 'Each bar is km per litre. The teal tick is that bus’s own normal. A red bar uses at least 10% more fuel than its normal.',
    ...(worst ? { open: { page: 'ConsoleBuses' as const, search: `?bus=${encodeURIComponent(worst.id)}`, label: 'Open bus' } } : {}),
  }, {
    key: 'fu-refills', label: 'Latest refills', mark: highN ? 'high for the kilometres' : 'match the kilometres',
    short: recent.length ? (highN ? `${highN} of the latest ${plural(recent.length, 'refill')} look high for the kilometres driven.` : 'The latest refills match the kilometres driven.') : 'No refills logged in this period.',
    means: 'Each refill is checked against the distance the bus drove since its last fill. “High for km” may be a typing slip, or fuel that went somewhere else.',
  }, {
    key: 'fu-quiet', label: 'Not logging', mark: 'no refill in 7 days',
    short: `${notLogging.length} of ${plural(f.buses.length, 'bus', 'buses')} drove but logged no refill in 7 days.`,
    means: notLogging.length ? 'Their fuel figures are estimates until the driver logs the next fill. I can remind those drivers for you.' : 'Every bus that drove this week logged a refill.',
  }] : [];

  usePage({
    points,
    key: 'ConsoleFuel', title: 'Fuel', zanaContext: `Fuel · ${periodWord.toLowerCase()}`,
    zanaIntro: 'I check every refill against the kilometres the bus drove.',
    // The period picked and the figures on the page, so "why is this so high?"
    // is about this window and the bus at the top of the list.
    zana: {
      window: periodWord.toLowerCase(),
      cards: [
        { label: `Fuel spend (${periodWord.toLowerCase()})`, value: `Ksh ${Math.round(spend).toLocaleString('en-GB')}` },
        ...(fleetKmpl != null ? [{ label: 'Fleet km per litre', value: fleetKmpl.toFixed(1) }] : []),
      ],
      focus: worst ? [platePlain(worst.plate)] : [],
    },
    prompts: [
      worst ? `Why is ${worst.plate} burning more fuel?` : 'Which bus uses the most fuel?',
      'Which buses are not logging refuels?',
      'What will fuel cost this term?',
    ],
  });

  /* ── the tour ── */
  const [step, setStep] = useState<number | null>(null);
  const tourBtn = useRef<HTMLButtonElement>(null);
  const nextBtn = useRef<HTMLButtonElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [narrow, setNarrow] = useState(false);
  const wantTour = params.get('tour') === '1';
  const started = useRef(false);
  useEffect(() => {
    if (wantTour && !f.loading && f.buses.length > 0 && !started.current) { started.current = true; setStep(0); }
  }, [wantTour, f.loading, f.buses.length]);
  const stopTour = useCallback(() => {
    setStep(null);
    if (params.get('tour')) { const n = new URLSearchParams(params); n.delete('tour'); setParams(n, { replace: true }); }
    setTimeout(() => tourBtn.current?.focus(), 0);
  }, [params, setParams]);
  const touring = step !== null;
  useEffect(() => {
    if (!touring) return;
    const w = (wrapRef.current?.closest('.tc') as HTMLElement | null)?.clientWidth ?? window.innerWidth;
    setNarrow(w < 900);
    nextBtn.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); stopTour(); } };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [touring, stopTour]);
  useEffect(() => { if (touring) nextBtn.current?.focus(); }, [step, touring]);

  const hasNote = over.length > 0;
  const noteText = hasNote ? (
    <>
      <b>{over.length === 1 ? `${over[0].bus.name} is using more fuel than its own normal` : `${over.length} buses are using more fuel than their own normal`}</b>
      {extraKsh != null ? `, about Ksh ${n0(extraKsh)} extra ${extraWhen}.` : '.'}
    </>
  ) : null;
  const step1 = {
    title: 'Start here',
    body: hasNote
      ? `This line is the short answer: ${over.length === 1 ? `${over[0].bus.name} is` : `${over.length} buses are`} using more fuel than normal${extraKsh != null ? `, about Ksh ${n0(extraKsh)} extra ${extraWhen}` : ''}.`
      : 'These four numbers are the short answer: what fuel cost, how much was logged, how far each litre went and which buses are not logging.',
  };
  const step2 = {
    title: over.length ? 'The red bars are the problem buses' : 'Each bar is one bus',
    body: `Each bar is km per litre. The teal tick is that bus’s own normal${worst ? `, so ${worst.name} is well below its own normal.` : '. Every bus is close to its own normal right now.'}`,
  };
  const step3 = { title: 'Then check the refills', body: 'Refills that match the kilometres are honest. Look at any marked “High for km”.' };

  const latest = refillsWin.slice(0, 6);
  const segBtn = (k: Period, label: string, disabled?: boolean) => (
    <button type="button" className={period === k ? 'on' : ''} aria-pressed={period === k} disabled={disabled}
      title={disabled ? 'Set your term dates in Settings to see this term' : undefined}
      style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined} onClick={() => setPeriod(k)}>{label}</button>
  );

  return (
    <PageBody loading={f.loading} skeleton={<PageSkeleton stats rows={7} />}>
      <div ref={wrapRef} className={'wrap' + (f.error ? ' stale' : '')}>
        {f.error && <StaleBanner what="fuel records" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h1 className="h1">Fuel</h1>
            <p className="sub">{periodWord} · {plural(refillsWin.length, 'refill')} logged by drivers</p>
          </div>
          <div className="row" style={{ gap: '10px', flexWrap: 'wrap' }}>
            <a className="btn s b-line" {...go('ConsoleFuelPrices')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.5" /></svg>
              Fuel prices
            </a>
            {f.buses.length > 0 && (
              <button ref={tourBtn} type="button" className="btn s b-line" onClick={() => setStep(0)} aria-pressed={touring}>
                <ZanaMark />Show me around
              </button>
            )}
            <div className="seg" role="group" aria-label="Period">
              {segBtn('7', '7 days')}
              {segBtn('30', '30 days')}
              {segBtn('term', 'Term', !termStart)}
            </div>
          </div>
        </div>

        {f.buses.length === 0 ? (
          <section className="panel">
            <Empty title="No buses yet" body="Add a bus and its driver. Refills the driver logs in the Trasse app appear here, checked against the kilometres driven.">
              <button type="button" className="btn b-ink" onClick={openAddBus}>Add a bus</button>
            </Empty>
          </section>
        ) : (
          <>
            {hasNote ? (
              <div style={step === 0 ? { ...RING, position: 'relative' } : { position: 'relative' }}>
                <ZanaNote onAsk={() => ask(worst && over.length === 1 ? `Why is ${worst.plate} burning more fuel?` : 'Why are these buses using more fuel than their normal?')}>
                  {noteText}
                </ZanaNote>
                {step === 0 && <TourCard step={0} {...step1} style={{ top: 'calc(100% + 12px)', left: '16px' }} />}
              </div>
            ) : null}

            <div style={{ position: 'relative', ...(step === 0 && !hasNote ? RING : {}) }}>
              <div className="stats">
                <div className="stat" data-zx="fu-spend">
                  <span className="k">Estimated spend</span>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <span className="big"><span className="unit">Ksh</span>{n0(spend)}</span>
                  </div>
                  <span className="sm">
                    {/* Good or bad only when every bus is logging: a bus that stopped makes the period look cheaper. */}
                    {spendDelta != null && <span className={'delta ' + (notLogging.length ? 'flat' : spendDelta > 0 ? 'dn-r' : 'up-g')}>{spendDelta > 0 ? '+' : '−'}{Math.abs(spendDelta)}%</span>}
                    <span style={{ color: 'var(--faint)' }}> {spendDelta != null ? `vs the ${period === '7' ? '7' : '30'} days before` : 'cost logged with each refill'}</span>
                  </span>
                </div>
                <div className="stat">
                  <span className="k">Litres logged</span>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <span className="big">{n0(litres)}</span>
                  </div>
                  <span className="sm"><span className="delta flat">{plural(refillsWin.length, 'refill')}</span></span>
                </div>
                <div className="stat">
                  <span className="k">Fleet km per litre</span>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <span className="big">{fleetKmpl != null ? fleetKmpl.toFixed(1) : '—'}</span>
                  </div>
                  <span className="sm">
                    {kmplDelta != null && Math.abs(kmplDelta) >= 0.05 && (
                      <span className={'delta ' + (kmplDelta < 0 ? 'dn-r' : 'up-g')}>{kmplDelta < 0 ? '−' : '+'}{Math.abs(kmplDelta).toFixed(1)}</span>
                    )}
                    <span style={{ color: 'var(--faint)' }}> {kmplDelta != null && Math.abs(kmplDelta) >= 0.05 ? 'vs the week before' : fleetKmpl != null ? `${n0(fleetKm)} km on ${n0(fleetL)} litres` : 'needs runs and refills'}</span>
                  </span>
                </div>
                <div className="stat" data-z="notlogging" data-zx="fu-quiet">
                  <span className="k">Not logging</span>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <span className="big">{notLogging.length} of {f.buses.length}</span>
                  </div>
                  <span className="sm">
                    <span className="delta flat">buses</span>
                    <span style={{ color: 'var(--faint)' }}> no refill in 7 days</span>
                  </span>
                </div>
              </div>
              {step === 0 && !hasNote && <TourCard step={0} {...step1} style={{ top: 'calc(100% + 12px)', left: '16px' }} />}
            </div>

            <div className="two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: '24px', alignItems: 'start' }}>
              <section className="panel" style={{ position: 'relative', ...(step === 1 ? RING : {}) }} data-zx="fu-bars">
                <div className="ph">
                  <div>
                    <h2 className="h2">Km per litre, by bus</h2>
                    <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>
                      Worst against its own normal first. The teal tick is each bus’s normal.
                      {kmClamped ? ' Uses the last 30 days of runs.' : ''}
                    </p>
                  </div>
                </div>
                {step === 1 && <TourCard step={1} {...step2} style={{ top: '72px', right: '20px' }} />}
                {ranked.length === 0 ? (
                  <Empty title="No km per litre yet" body="Each bus needs a finished run and a logged refill in this period. Drivers log refills in the Trasse app when they fill up." />
                ) : (
                  <div style={{ padding: '10px 20px 16px' }}>
                    {ranked.map((x) => {
                      const bad = x.normal != null && (x.kmpl as number) < x.normal * 0.9;
                      return (
                        <div key={x.bus.id} data-z={`fuel:${x.bus.id}`} style={{ borderRadius: '8px', display: 'grid', gridTemplateColumns: '170px minmax(0, 1fr) 96px', gap: '16px', alignItems: 'center', padding: '8px 0' }}>
                          <div className="who">
                            <b><a {...go('ConsoleBuses', `?bus=${encodeURIComponent(x.bus.id)}`)}>{x.bus.name}</a></b>
                            {plateBeside(x.bus) && <span>{x.bus.plate}</span>}
                          </div>
                          <div style={{ position: 'relative', height: '18px', background: 'var(--s2)', borderRadius: '4px' }}
                            role="img" aria-label={`${x.bus.name}: ${(x.kmpl as number).toFixed(1)} km per litre${x.normal != null ? `, normal ${x.normal.toFixed(1)}` : ''}`}>
                            <div style={{ position: 'absolute', left: '0', top: '0', bottom: '0', width: `${Math.min(100, ((x.kmpl as number) / scale) * 100).toFixed(1)}%`, background: bad ? 'var(--bad)' : 'var(--ink)', borderRadius: '4px' }} />
                            {x.normal != null && (
                              <div title="Its normal" style={{ position: 'absolute', top: '-4px', bottom: '-4px', left: `${Math.min(100, (x.normal / scale) * 100).toFixed(1)}%`, width: '2px', background: 'var(--teal-ink)' }} />
                            )}
                          </div>
                          <div className="num" style={{ textAlign: 'right' }}>
                            <b>{(x.kmpl as number).toFixed(1)}</b>
                            <span className="sm" style={{ color: 'var(--muted)' }}> / {x.normal != null ? x.normal.toFixed(1) : '—'}</span>
                          </div>
                        </div>
                      );
                    })}
                    {(noKmpl > 0 || ranked.some((x) => x.normal == null)) && (
                      <p className="sm" style={{ margin: '8px 0 0', color: 'var(--faint)' }}>
                        {noKmpl > 0 ? `${plural(noKmpl, 'bus has', 'buses have')} no runs and refills to compare in this period. ` : ''}
                        {ranked.some((x) => x.normal == null) ? 'A “/ —” means no normal is set for that bus yet.' : ''}
                      </p>
                    )}
                  </div>
                )}
              </section>

              <section className="panel" style={{ position: 'relative', ...(step === 2 ? RING : {}) }} data-z="refills" data-zx="fu-refills">
                <div className="ph" style={{ position: 'relative' }}>
                  <h2 className="h2">Latest refills</h2>
                  <span className="sm" style={{ color: 'var(--muted)' }}>From the driver app</span>
                  {step === 2 && (
                    <TourCard step={2} {...step3} style={narrow ? { top: 'calc(100% + 12px)', right: '0' } : { top: '64px', right: 'calc(100% + 14px)' }} />
                  )}
                </div>
                {latest.length === 0 ? (
                  <Empty title="No refills in this period" body="Drivers log each refill in the Trasse app when they fill up. It appears here, checked against the kilometres driven.">
                    {period !== '30' && <button type="button" className="btn s b-line" onClick={() => setPeriod('30')}>Show 30 days</button>}
                  </Empty>
                ) : (
                  <div className="tblbox">
                    <table className="t fit" style={{ minWidth: '0' }}>
                      <tbody>
                        {latest.map((r) => (
                          <tr key={r.id}>
                            <td>
                              <div className="who">
                                <b>{r.busPlate}</b>
                                <span>{[r.at ? dayMonth(r.at) : '', r.station].filter(Boolean).join(' · ') || '—'}</span>
                              </div>
                            </td>
                            <td className="r num">
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', padding: '6px 0' }}>
                                <span>{[r.litres ? `${n0(r.litres)} L` : '', r.amount ? `Ksh ${n0(r.amount)}` : ''].filter(Boolean).join(' · ') || '—'}</span>
                                {r.verdict === 'ok' && <span className="chip c-ok">Matches km</span>}
                                {r.verdict === 'high' && <span className="chip c-warn">High for km</span>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          </>
        )}

        {touring && f.buses.length > 0 && (
          <div style={{ position: 'sticky', bottom: '20px', display: 'flex', justifyContent: 'center', marginTop: '24px', zIndex: 6 }}>
            <div className="row zin-a" role="group" aria-label="Zana is showing you around" style={{ gap: '10px', background: 'var(--s1)', border: '1px solid var(--z-line)', borderRadius: '14px', padding: '8px 8px 8px 14px', boxShadow: '0 18px 40px -18px rgba(17,21,32,.35)', flexWrap: 'wrap' }}>
              <span aria-hidden="true" style={{ width: '22px', height: '22px', borderRadius: '7px', background: 'var(--z)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '3px', flex: 'none' }}>
                <i style={{ width: '4px', height: '5px', borderRadius: '3px', background: '#fff', display: 'block' }} />
                <i style={{ width: '4px', height: '5px', borderRadius: '3px', background: '#fff', display: 'block' }} />
              </span>
              <span style={{ fontWeight: '600' }}>Zana is showing you around</span>
              <span className="mono" style={{ padding: '0 6px' }} aria-live="polite">{(step as number) + 1} of 3</span>
              <button type="button" className="btn s b-line" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, (s ?? 0) - 1))}>Back</button>
              <button ref={nextBtn} type="button" className="btn s b-ink" onClick={() => (step === 2 ? stopTour() : setStep((s) => (s ?? 0) + 1))}>
                {step === 2 ? 'Done' : 'Next'}
              </button>
              <button type="button" className="ibtn" aria-label="Stop the tour" onClick={stopTour} style={{ width: '32px', height: '32px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </PageBody>
  );
};

export default Fuel;
